import { cloneDeep, isEqual } from "lodash";
import React, { Component } from "react";
import Draggable from "react-draggable";
import LoadingOverlay from "react-loading-overlay";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import Portal from "../../common/components/Portal";
import Loader from "../../common/components/Loader";
import {
    ASSET_VIEW_BANDS,
    ASSET_VIEW_ALWAYS_VISIBLE_KEYS,
    ASSET_VIEW_FORM_FIELD_ORDER,
    getAssetViewFieldBand,
    getAssetViewFieldBandLabel,
    isDynamicBandConfigKey
} from "./assetViewFieldConfig";
import { isExplicitlyHidden } from "./assetViewFieldConfigUtils";
import "../../building/fieldVisibility/BuildingFieldVisibilityModal.css";

const BAND_ORDER = [
    ASSET_VIEW_BANDS.BASIC,
    ASSET_VIEW_BANDS.GEO,
    ASSET_VIEW_BANDS.EXTENDED,
    ASSET_VIEW_BANDS.QA,
    ASSET_VIEW_BANDS.PARENT_CHILD,
    ASSET_VIEW_BANDS.ADDITIONAL,
    ASSET_VIEW_BANDS.BOILER,
    ASSET_VIEW_BANDS.AUXILIARY
];

const ALL_STATIC_BANDS = [...BAND_ORDER];

const getFieldLabel = (key, config = {}) => {
    if (config[key]?.label) return config[key].label;
    const normalized = String(key)
        .replace(/^df__/, "")
        .replace(/_/g, " ")
        .trim();
    return normalized.replace(/\b\w/g, char => char.toUpperCase()) || key;
};

export class AssetFieldVisibilityModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            viewFilter: "all",
            searchKey: "",
            fieldConfig: cloneDeep(props.fieldConfig),
            initialData: cloneDeep(props.fieldConfig),
            showConfirmModal: false,
            applying: false,
            expandedBands: {}
        };
    }

    handleDropdownChange = event => {
        this.setState({ viewFilter: event.target.value });
    };

    handleSearch = event => {
        this.setState({ searchKey: event.target.value });
    };

    isBandExpanded = band => !!this.state.expandedBands[band] || !!this.state.searchKey?.trim()?.length;

    toggleBand = band =>
        this.setState(prev => ({
            expandedBands: { ...prev.expandedBands, [band]: !prev.expandedBands[band] }
        }));

    setAllBandsExpanded = (bands, expand) => {
        const expandedBands = {};
        if (expand) bands.forEach(band => (expandedBands[band] = true));
        this.setState({ expandedBands });
    };

    isRequired = key => this.props.requiredKeys?.has?.(key);

    isToggleable = key => !this.isRequired(key) && !ASSET_VIEW_ALWAYS_VISIBLE_KEYS.has(key);

    handleToggleField = key => {
        if (!this.isToggleable(key)) return;
        this.setState(prev => {
            const currentlyVisible = prev.fieldConfig.config[key]?.isVisible !== false;
            return {
                fieldConfig: {
                    ...prev.fieldConfig,
                    config: {
                        ...prev.fieldConfig.config,
                        [key]: {
                            ...prev.fieldConfig.config[key],
                            isVisible: !currentlyVisible
                        }
                    }
                }
            };
        });
    };

    handleSelectAll = selectAll => {
        const { fieldConfig } = this.state;
        const updatedConfig = { ...fieldConfig.config };
        fieldConfig.keys.forEach(key => {
            if (!this.isToggleable(key)) return;
            updatedConfig[key] = { ...updatedConfig[key], isVisible: selectAll };
        });
        this.setState({ fieldConfig: { ...fieldConfig, config: updatedConfig } });
    };

    allToggleableSelected = () => {
        const { fieldConfig } = this.state;
        const toggleable = fieldConfig.keys.filter(k => this.isToggleable(k));
        if (!toggleable.length) return true;
        return toggleable.every(k => fieldConfig.config[k]?.isVisible);
    };

    getBandToggleableKeys = keys => keys.filter(k => this.isToggleable(k));

    isBandAllSelected = keys => {
        const { fieldConfig } = this.state;
        const toggleable = this.getBandToggleableKeys(keys);
        if (!toggleable.length) return true;
        return toggleable.every(k => fieldConfig.config[k]?.isVisible);
    };

    isBandPartiallySelected = keys => {
        const { fieldConfig } = this.state;
        const toggleable = this.getBandToggleableKeys(keys);
        if (toggleable.length < 2) return false;
        const visibleCount = toggleable.filter(k => fieldConfig.config[k]?.isVisible).length;
        return visibleCount > 0 && visibleCount < toggleable.length;
    };

    handleSelectBand = (keys, selectAll) => {
        const toggleable = this.getBandToggleableKeys(keys);
        if (!toggleable.length) return;
        this.setState(prev => {
            const updatedConfig = { ...prev.fieldConfig.config };
            toggleable.forEach(key => {
                updatedConfig[key] = { ...updatedConfig[key], isVisible: selectAll };
            });
            return {
                fieldConfig: { ...prev.fieldConfig, config: updatedConfig }
            };
        });
    };

    setBandCheckboxRef = (el, keys) => {
        if (el) {
            el.indeterminate = this.isBandPartiallySelected(keys);
        }
    };

    handleSave = async () => {
        if (this.state.applying) return;
        this.setState({ applying: true });
        try {
            await this.props.onApply(this.state.fieldConfig);
            this.props.onCancel();
        } finally {
            this.setState({ applying: false });
        }
    };

    handleReset = async () => {
        if (this.state.applying || typeof this.props.onReset !== "function") return;
        this.setState({ applying: true });
        try {
            await this.props.onReset();
        } finally {
            this.setState({ applying: false });
        }
    };

    confirmCancel = () => {
        if (this.state.applying) return;
        if (isEqual(this.state.fieldConfig, this.state.initialData)) {
            this.props.onCancel();
        } else {
            this.setState({ showConfirmModal: true });
        }
    };

    renderConfirmationModal = () => {
        if (!this.state.showConfirmModal) return null;
        return (
            <Portal
                body={
                    <ConfirmationModal
                        heading="Do you want to cancel and lose all changes?"
                        type="cancel"
                        message="This action cannot be reverted, are you sure that you need to cancel?"
                        onNo={() => this.setState({ showConfirmModal: false })}
                        onYes={() => {
                            this.setState({ showConfirmModal: false });
                            this.props.onCancel();
                        }}
                    />
                }
                onCancel={() => this.setState({ showConfirmModal: false })}
            />
        );
    };

    getFilteredKeys = () => {
        const { fieldConfig, viewFilter, searchKey } = this.state;
        const keySet = new Set(fieldConfig.keys || []);
        Object.keys(fieldConfig.config || {}).forEach(k => keySet.add(k));
        let keyList = [...keySet].filter(k => !ASSET_VIEW_ALWAYS_VISIBLE_KEYS.has(k));
        if (viewFilter === "visible") {
            keyList = keyList.filter(k => !isExplicitlyHidden(fieldConfig.config[k]?.isVisible));
        } else if (viewFilter === "inVisible") {
            keyList = keyList.filter(k => isExplicitlyHidden(fieldConfig.config[k]?.isVisible));
        }
        if (searchKey?.trim()?.length) {
            const q = searchKey.toLowerCase();
            keyList = keyList.filter(k => getFieldLabel(k, fieldConfig.config).toString().toLowerCase().includes(q));
        }
        return keyList;
    };

    groupKeysByBand = keyList => {
        const { fieldConfig } = this.state;
        const layoutContext = this.props.layoutContext || "view";
        const keyOrder =
            layoutContext === "form"
                ? [...ASSET_VIEW_FORM_FIELD_ORDER, ...(fieldConfig.keys || [])]
                : fieldConfig.keys || [];
        const sortKeys = keys =>
            [...keys].sort((a, b) => {
                const ai = keyOrder.indexOf(a);
                const bi = keyOrder.indexOf(b);
                if (ai === -1 && bi === -1) return 0;
                if (ai === -1) return 1;
                if (bi === -1) return -1;
                return ai - bi;
            });
        const groups = {};
        keyList.forEach(key => {
            const band = getAssetViewFieldBand(key, fieldConfig, layoutContext) || "Other";
            if (!groups[band]) groups[band] = [];
            groups[band].push(key);
        });
        const ordered = [];
        BAND_ORDER.forEach(band => {
            if (groups[band]?.length) ordered.push([band, sortKeys(groups[band])]);
        });
        Object.entries(groups)
            .filter(([band]) => !ALL_STATIC_BANDS.includes(band))
            .sort(([a], [b]) => {
                const aDyn = isDynamicBandConfigKey(a);
                const bDyn = isDynamicBandConfigKey(b);
                if (aDyn !== bDyn) return aDyn ? 1 : -1;
                return getAssetViewFieldBandLabel(a, fieldConfig).localeCompare(getAssetViewFieldBandLabel(b, fieldConfig));
            })
            .forEach(([band, keys]) => {
                ordered.push([band, sortKeys(keys)]);
            });
        return ordered.filter(([, keys]) => keys.length > 0);
    };

    render() {
        const { showReset } = this.props;
        const { viewFilter, searchKey, fieldConfig, applying } = this.state;
        const keyList = this.getFilteredKeys();
        const grouped = this.groupKeysByBand(keyList);
        const allSelected = this.allToggleableSelected();
        const hasToggleable = (fieldConfig.keys || []).some(k => this.isToggleable(k));
        const allBandsExpanded = grouped.length > 0 && grouped.every(([b]) => !!this.state.expandedBands[b]);

        return (
            <>
                {this.renderConfirmationModal()}
                <div className="modal building-field-visibility-modal" style={{ display: "block" }} tabIndex="-1">
                    <Draggable handle=".bfv-header" cancel=".not-draggable" disabled={applying}>
                        <div className="modal-dialog bfv-dialog" role="document">
                            <LoadingOverlay
                                active={applying}
                                spinner={<Loader />}
                                text="Saving…"
                                fadeSpeed={150}
                                className="bfv-loading-overlay"
                            >
                            <div className="modal-content bfv-card">
                                <div className="bfv-header draggable">
                                    <h2 className="bfv-title">Show / hide fields</h2>
                                    <div className="bfv-header-actions not-draggable">
                                        <select
                                            className="bfv-filter form-control"
                                            onChange={this.handleDropdownChange}
                                            value={viewFilter}
                                            disabled={applying}
                                        >
                                            <option value="all">All fields</option>
                                            <option value="visible">Visible</option>
                                            <option value="inVisible">Hidden</option>
                                        </select>
                                        <button
                                            type="button"
                                            className="bfv-close"
                                            onClick={this.confirmCancel}
                                            aria-label="Close"
                                            disabled={applying}
                                        >
                                            <img src="/img/close.svg" alt="" width={14} height={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="bfv-body not-draggable">
                                    <div className="bfv-search-wrap">
                                        <i className="fas fa-search" aria-hidden />
                                        <input
                                            type="text"
                                            className="bfv-search"
                                            onChange={this.handleSearch}
                                            value={searchKey}
                                            placeholder="Search fields…"
                                            disabled={applying}
                                        />
                                    </div>
                                    <div className="afv-list-controls not-draggable">
                                        {viewFilter === "all" && hasToggleable ? (
                                            <label className="bfv-select-all">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    disabled={applying}
                                                    onChange={() => this.handleSelectAll(!allSelected)}
                                                />
                                                Select all
                                            </label>
                                        ) : (
                                            <span />
                                        )}
                                        {grouped.length ? (
                                            <button
                                                type="button"
                                                className="afv-expand-toggle"
                                                disabled={applying}
                                                onClick={() =>
                                                    this.setAllBandsExpanded(grouped.map(([b]) => b), !allBandsExpanded)
                                                }
                                            >
                                                {allBandsExpanded ? "Collapse all" : "Expand all"}
                                            </button>
                                        ) : null}
                                    </div>
                                    <div className="building-field-visibility-groups">
                                        {grouped.length ? (
                                            grouped.map(([band, keys]) => {
                                                const bandLabel = getAssetViewFieldBandLabel(band, fieldConfig);
                                                const bandToggleable = this.getBandToggleableKeys(keys);
                                                const bandAllSelected = this.isBandAllSelected(keys);
                                                const showBandToggle =
                                                    viewFilter === "all" && bandToggleable.length > 0;
                                                const expanded = this.isBandExpanded(band);
                                                return (
                                                <div key={band} className="bfv-band">
                                                    <div className={`bfv-band-header afv-band-header${expanded ? " afv-band-header--open" : ""}`}>
                                                        {showBandToggle ? (
                                                            <input
                                                                type="checkbox"
                                                                className="afv-band-check"
                                                                checked={bandAllSelected}
                                                                ref={el => this.setBandCheckboxRef(el, keys)}
                                                                disabled={applying}
                                                                onChange={() =>
                                                                    this.handleSelectBand(keys, !bandAllSelected)
                                                                }
                                                            />
                                                        ) : null}
                                                        <button
                                                            type="button"
                                                            className="afv-band-toggle"
                                                            disabled={applying}
                                                            aria-expanded={expanded}
                                                            onClick={() => this.toggleBand(band)}
                                                        >
                                                            <span className="bfv-band-title">{bandLabel}</span>
                                                            <i
                                                                className={`fas fa-chevron-${expanded ? "down" : "right"} afv-band-caret`}
                                                                aria-hidden
                                                            />
                                                        </button>
                                                    </div>
                                                    {expanded ? (
                                                        <div className="bfv-field-grid">
                                                            {keys.map(key => {
                                                                const locked = !this.isToggleable(key);
                                                                const label = getFieldLabel(key, fieldConfig.config);
                                                                const checked = !isExplicitlyHidden(fieldConfig.config[key]?.isVisible);
                                                                return (
                                                                    <label
                                                                        key={key}
                                                                        className={`bfv-field${locked ? " bfv-field--locked" : ""}`}
                                                                        onClick={e => {
                                                                            if (locked) {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={checked}
                                                                            disabled={locked || applying}
                                                                            onChange={() => this.handleToggleField(key)}
                                                                        />
                                                                        <span>{label}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                );
                                            })
                                        ) : (
                                            <div className="bfv-empty">No fields match your search</div>
                                        )}
                                    </div>
                                </div>
                                <div className="bfv-footer not-draggable">
                                    {showReset ? (
                                        <button
                                            type="button"
                                            className="bfv-reset"
                                            onClick={this.handleReset}
                                            disabled={applying}
                                        >
                                            Reset to defaults
                                        </button>
                                    ) : (
                                        <span />
                                    )}
                                    <div className="bfv-footer-btns">
                                        <button
                                            type="button"
                                            className="bfv-btn bfv-btn--ghost"
                                            onClick={this.confirmCancel}
                                            disabled={applying}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="bfv-btn bfv-btn--primary"
                                            onClick={this.handleSave}
                                            disabled={applying}
                                        >
                                            {applying ? "Applying…" : "Apply"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            </LoadingOverlay>
                        </div>
                    </Draggable>
                </div>
            </>
        );
    }
}

export default AssetFieldVisibilityModal;
